import { inject, Injectable } from '@angular/core';
import { MessageRequest } from '@skyeng/libs/skypro/messages';
import { Observable } from 'rxjs';
import { AbstractPollApiService } from '@skyeng/libs/skypro/misc';
import { CommunicationFormService } from '../components/communication-form/communication-form.service';
import { CommunicationApiService } from './communication-api.service';
import { Communication, CommunicationApiResponseSuccess, MessageRequestWithMeta } from '../interface';
import { CommunicationLikeService } from './communication-like.service';

@Injectable({ providedIn: 'root' })
export class CommunicationService
  extends AbstractPollApiService
  implements CommunicationFormService, CommunicationLikeService
{
  protected communicationId?: number;

  private communicationApiService = inject(CommunicationApiService);

  /**
   * Принудительно выполняет итерацию поллинга,
   * другими словами принудительно загружает коммуникацию из апи
   */
  forcePoll(): void {
    this.fireRefresh();
  }

  createThread(message: MessageRequest): Observable<CommunicationApiResponseSuccess> {
    if (!this.communicationId) {
      throw new Error('communicationId is not defined');
    }
    return this.wrapWithLoading(this.communicationApiService.createThread(message, this.communicationId));
  }

  replyInThread(message: MessageRequestWithMeta, threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.replyInThread(message, threadId));
  }

  editThread(message: MessageRequest, threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.editThread(message, threadId));
  }

  editMessageInThread(message: MessageRequest, messageId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.editMessageInThread(message, messageId));
  }

  pollCommunication(
    communicationId: number,
    lastThreadId: number | null,
    sortByOwnThreads = true,
  ): Observable<Communication> {
    this.communicationId = communicationId;
    return this.wrapWithPolling(
      this.communicationApiService.getCommunication(communicationId, lastThreadId, sortByOwnThreads),
      false,
    );
  }

  getCommunication(communicationId: number, lastThreadId: number | null): Observable<Communication> {
    this.communicationId = communicationId;
    return this.wrapWithLoading(this.communicationApiService.getCommunication(communicationId, lastThreadId));
  }

  deleteThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.deleteThread(threadId));
  }

  pinThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.pinThread(threadId));
  }

  unpinThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.unpinThread(threadId));
  }

  hideThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.hideThread(threadId));
  }

  showThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.showThread(threadId));
  }

  deleteMessageInThread(messageId: number): Observable<CommunicationApiResponseSuccess> {
    return this.wrapWithLoading(this.communicationApiService.deleteMessageInThread(messageId));
  }

  switchLike(isLiked: boolean, contentId: number): Observable<CommunicationApiResponseSuccess> {
    return isLiked
      ? this.communicationApiService.createLike(contentId)
      : this.communicationApiService.deleteLike(contentId);
  }

  sendReport(contentId: number, comment: string): Observable<CommunicationApiResponseSuccess> {
    return this.communicationApiService.sendReport(contentId, comment);
  }
}
